import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/firebase/api-client";

/**
 * Le signe de vie de cet ecran, et ce qu'il en dit.
 *
 * Toutes les minutes, l'ecran previent le serveur qu'il est ouvert. C'est ce
 * signal qui alimente la liste des profils connectes cote administration :
 * sans lui, personne ne peut savoir qui travaille.
 *
 * Le point affiche ici ne dit PAS « tu es connecte » — la personne le sait,
 * elle regarde son ecran. Il dit « l'application parle encore au serveur ».
 * C'est exactement ce qu'on veut voir quand le wifi du magasin lache : le
 * point passe au rouge avant qu'on ait compris pourquoi plus rien ne bouge.
 *
 * -------------------------------------------------------------------------
 *  DEUX PRESENTATIONS, UN SEUL COMPOSANT
 * -------------------------------------------------------------------------
 *
 *    par defaut   une ligne discrete, sous le nom, dans la barre laterale
 *    barre        un bandeau pleine largeur, avec le nom
 *
 *  Le bandeau existe parce qu'en affichage etroit la barre laterale
 *  disparait : sans lui, ni le nom ni l'etat ne seraient visibles sur un
 *  telephone. C'est la ou l'information compte le plus — c'est sur un
 *  telephone qu'on perd le reseau.
 *
 *  `sombre` adapte les couleurs a un fond fonce (la barre laterale de
 *  l'admin). Il ne s'applique qu'a la presentation par defaut : le bandeau
 *  a son propre fond, clair, quelle que soit la mise en page.
 *
 * -------------------------------------------------------------------------
 *  POURQUOI CE COMPOSANT A ETE REPRIS
 * -------------------------------------------------------------------------
 *
 *  Il passait au ROUGE a chaque ouverture de page, pendant une minute, sans
 *  qu'il y ait la moindre coupure.
 *
 *  La cause n'etait pas ici mais dans api-client : au demarrage,
 *  l'application attend le jeton d'identification de Firebase pendant
 *  3 secondes au plus, puis abandonne. Or ce composant envoyait son premier
 *  signal IMMEDIATEMENT, avant meme que Firebase ait fini de restaurer la
 *  session. Sur un rechargement force, ou une connexion lente, les
 *  3 secondes ne suffisent pas : l'appel partait sans jeton et echouait.
 *
 *  Le composant affichait alors du rouge — puis attendait 60 SECONDES avant
 *  de retenter. Le deuxieme essai reussissait toujours.
 *
 *  Un voyant qui se trompe une minute sur deux cesse d'etre lu. Et le jour
 *  ou il signale une vraie coupure, plus personne ne le regarde. C'est le
 *  seul vrai danger dans cette histoire : les commandes, elles, arrivent par
 *  webhook cote serveur et ne dependent d'aucun navigateur ouvert.
 *
 *  Trois changements, donc :
 *
 *    1. TROIS ETATS au lieu de deux. Un gris « connexion… » pour le moment
 *       ou l'on ne sait pas encore. Ne rien affirmer vaut mieux qu'affirmer
 *       faux.
 *
 *    2. On ne passe au ROUGE qu'apres DEUX echecs de suite. Un premier rate
 *       au demarrage ne prouve rien ; deux d'affilee, si.
 *
 *    3. Apres un echec, on retente vite — 5 s, puis 15 s — au lieu
 *       d'attendre la minute suivante. Une vraie coupure est signalee en
 *       ~20 secondes au lieu de 60, et une fausse alerte ne dure jamais.
 *
 *  Le rythme de croisiere, lui, ne change pas : un signal par minute.
 *  L'inonder de requetes ne rendrait personne plus connecte.
 * -------------------------------------------------------------------------
 */

/** Le rythme normal, quand tout va bien. */
const PERIODE_NORMALE = 60000;

/**
 * Apres un echec, on ne patiente pas une minute pour savoir. Le premier
 * nouvel essai arrive vite (le cas frequent : Firebase avait juste besoin
 * d'une seconde de plus), le second laisse un peu de temps a un reseau qui
 * revient.
 */
const DELAIS_APRES_ECHEC = [5000, 15000];

/** Deux echecs de suite avant d'alarmer. Un seul ne prouve rien. */
// TROIS ECHECS, ET NON DEUX.
//
// Deux battements manques suffisaient a afficher « hors ligne » a quelqu'un
// qui travaillait : une coupure de quelques secondes dans le magasin, et le
// bandeau rouge apparaissait sans que rien ne soit casse. Le troisieme essai
// coute une minute d'attente et evite la fausse alerte.
const ECHECS_AVANT_ALERTE = 3;

type Etat = "inconnu" | "enLigne" | "horsLigne";

export default function EtatConnexion({
  sombre = false,
  nom,
  barre = false,
}: {
  sombre?: boolean;
  /** Le nom a afficher dans le bandeau. Ignore hors du mode barre. */
  nom?: string;
  /** Bandeau pleine largeur, pour l'affichage etroit. */
  barre?: boolean;
}) {
  const [etat, setEtat] = useState<Etat>("inconnu");

  // Le compteur d'echecs vit dans une ref, pas dans un state : le modifier
  // ne doit pas relancer l'effet, sinon on reconstruirait la minuterie a
  // chaque battement.
  const echecs = useRef(0);

  /**
   * LA RAISON DU ROUGE.
   *
   * « Hors ligne » sans explication ne se depanne pas : on ne sait pas si le
   * reseau du magasin a coupe, si la session a expire, ou si le serveur a
   * refuse le compte. Le dernier message d'erreur est garde ici, affiche au
   * survol et ecrit dans la console — c'est ce qui permet de trancher au
   * lieu de deviner.
   */
  const [raison, setRaison] = useState<string>("");

  useEffect(() => {
    let annule = false;
    let minuterie: number | undefined;

    const programmer = (delai: number) => {
      window.clearTimeout(minuterie);
      minuterie = window.setTimeout(() => void battre(), delai);
    };

    async function battre() {
      if (annule) return;

      try {
        await api.auth.signalDeVie();
        if (annule) return;

        echecs.current = 0;
        setRaison("");
        setEtat("enLigne");
        programmer(PERIODE_NORMALE);
      } catch (e: any) {
        if (annule) return;

        echecs.current += 1;
        const message = String(e?.message || e || "erreur inconnue");
        setRaison(message);
        console.warn(
          `[PRESENCE] battement ${echecs.current} en echec : ${message}`,
        );

        // Sous le seuil, on ne dit rien plutot que de dire faux. Le gris
        // « connexion… » est honnete : on ne sait pas encore.
        if (echecs.current >= ECHECS_AVANT_ALERTE) {
          setEtat("horsLigne");
        }

        // Les premiers essais se rapprochent, puis on retombe sur le rythme
        // normal : marteler un serveur injoignable ne le ramene pas.
        const i = Math.min(echecs.current - 1, DELAIS_APRES_ECHEC.length - 1);
        programmer(
          echecs.current <= DELAIS_APRES_ECHEC.length
            ? DELAIS_APRES_ECHEC[i]
            : PERIODE_NORMALE,
        );
      }
    }

    void battre();

    // Revenir sur l'onglet doit rafraichir tout de suite : au retour d'une
    // heure de veille, attendre la minute suivante afficherait un rouge
    // qui n'est plus vrai.
    const auRetour = () => {
      if (document.visibilityState === "visible") void battre();
    };
    document.addEventListener("visibilitychange", auRetour);

    return () => {
      annule = true;
      window.clearTimeout(minuterie);
      document.removeEventListener("visibilitychange", auRetour);
    };
  }, []);

  const couleurPoint =
    etat === "enLigne"
      ? "bg-green-500"
      : etat === "horsLigne"
        ? "bg-red-500"
        : "bg-gray-400";

  const libelle =
    etat === "enLigne"
      ? "connecté"
      : etat === "horsLigne"
        ? "hors ligne"
        : "connexion…";

  // ------------------------------------------------------------- le bandeau
  if (barre) {
    // Sur un fond clair, le vert et le rouge se suffisent a eux-memes ; le
    // gris d'attente reste discret pour ne pas attirer l'oeil sur un etat
    // qui ne dure qu'une seconde.
    const couleurTexteBarre =
      etat === "enLigne"
        ? "text-green-700"
        : etat === "horsLigne"
          ? "text-red-700"
          : "text-gray-400";

    return (
      <div className="flex items-center justify-between gap-3 px-4 py-2 bg-white border-b border-gray-200">
        <p className="text-sm font-semibold text-gray-800 truncate">
          {nom || " "}
        </p>
        <p
          className={`flex items-center gap-1.5 text-[11px] font-semibold shrink-0 ${couleurTexteBarre}`}
        >
          <span
            className={`inline-block w-2 h-2 rounded-full ${couleurPoint} ${
              etat === "inconnu" ? "animate-pulse" : ""
            }`}
          />
          <span title={etat === "horsLigne" && raison ? raison : undefined}>
            {libelle}
          </span>
        </p>
      </div>
    );
  }

  // --------------------------------------------- la ligne sous le nom
  const couleurTexte =
    etat === "enLigne"
      ? sombre
        ? "text-green-300"
        : "text-green-600"
      : etat === "horsLigne"
        ? sombre
          ? "text-red-300"
          : "text-red-600"
        : sombre
          ? "text-white/60"
          : "text-gray-400";

  return (
    <p
      className={`mt-0.5 flex items-center gap-1.5 text-[11px] font-semibold ${couleurTexte}`}
    >
      <span
        className={`inline-block w-2 h-2 rounded-full ${couleurPoint} ${
          etat === "inconnu" ? "animate-pulse" : ""
        }`}
      />
      {libelle}
    </p>
  );
}
